<template>
  <ViewHeader>
    <template v-if="!favouriteStationsError" #tools>
      <ButtonGroup>
        <Button
          v-if="favouriteStationsSyncSupported"
          type="button"
          :text="$t('favourites.sync')"
          rounded
          hide-text
          @click="() => initFavouriteStations()"
        >
          <template #icon>
            <i-mdi-reload />
          </template>
        </Button>
        <Button
          type="button"
          :text="editing === true ? $t('finish') : $t('edit')"
          hide-text
          rounded
          :disabled="!favouriteStations.length"
          @click="() => (editing = !editing)"
        >
          <template #icon>
            <i-mdi-check v-if="editing === true" />
            <i-mdi-pencil v-else />
          </template>
        </Button>
      </ButtonGroup>
    </template>
  </ViewHeader>

  <Block>
    <Spinner
      v-if="favouriteStationsLoading === true"
      size="large"
      color="primary"
    />
    <Notification
      v-else-if="favouriteStationsError"
      :text="$t(favouriteStationsError)"
      type="error"
    >
      <template #actions>
        <Button
          :text="$t('tryAgain')"
          @click="() => initFavouriteStations()"
          size="small"
          color="error"
        >
          <template #icon>
            <i-mdi-refresh />
          </template>
        </Button>
      </template>
    </Notification>
    <template v-else>
      <Notification
        v-if="!favouriteStations.length"
        :text="$t('routes.favourites.noItems')"
      >
        <template #actions>
          <Button
            :text="$t('routes.search.menuTitle')"
            color="info"
            size="small"
            @click="router.push({ name: 'search' })"
          >
            <template #icon>
              <i-mdi-search />
            </template>
          </Button>
          <Button
            :text="$t('routes.browse.menuTitle')"
            color="info"
            size="small"
            @click="router.push({ name: 'browse' })"
          >
            <template #icon>
              <i-mdi-book-open-page-variant />
            </template>
          </Button>
        </template>
      </Notification>
      <template v-else>
        <StationList ref="stationList">
          <StationListItem
            v-for="item of favouriteStations"
            :key="item.id"
            ref="stationListItemComponents"
            :station="item"
            :show-tags="true"
            :show-country="false"
            editable
            :editing="editing"
            @click="(station: Station) => setStation(station)"
            @remove="(station: Station) => toggleFavouriteStation(station)"
          />
        </StationList>
        <Notification
          v-if="!userIsLoggedIn"
          :text="$t('myAccount.summary')"
          type="info"
        >
          <template #actions>
            <Button
              :text="$t('myAccount.login')"
              @click="() => login()"
              size="small"
              color="info"
            >
              <template #icon>
                <i-mdi-user />
              </template>
            </Button>
          </template>
        </Notification>
      </template>
    </template>
  </Block>
</template>

<script setup lang="ts">
import type { Station } from '@/types'
import { ref, onActivated, onDeactivated } from 'vue'
import { useRouter } from 'vue-router'
import { useFavouriteStations } from '@/lib/favouriteStations'
import { setStation } from '@/lib/player'
import { userIsLoggedIn, login } from '@/lib/user-session'
import Button from '@/components/Button.vue'

const router = useRouter()
const {
  favouriteStationsSyncSupported,
  favouriteStationsLoading,
  initFavouriteStations,
  favouriteStations,
  favouriteStationsError,
  toggleFavouriteStation,
} = useFavouriteStations()

const editing = ref<boolean>(false)
let syncInterval: ReturnType<typeof setTimeout>

onActivated(() => {
  initFavouriteStations(true)

  if (favouriteStationsSyncSupported)
    syncInterval = setInterval(
      () => {
        if (favouriteStationsSyncSupported.value === true) {
          initFavouriteStations(true)
        }
      },
      1000 * 60 * 60, // One hour
    )
})

onDeactivated(() => {
  clearInterval(syncInterval)
})
</script>
